import * as React from 'react';
import { styled } from '@linaria/react';
import { Play, Pause } from 'lucide-react';

import Space from '@/components/Space';
import ButtonPlayful from '@/components/ButtonPlayful';
import Demo, { DemoSliderControl } from '@/components/Demo';

function GlassyEdge() {
  const [playState, setPlayState] = React.useState('running');

  return (
    <Demo
      initialValues={{
        thickness: 3,
        radius: 8,
      }}
      controlPosition="top"
      outputWrapper={null}
      controls={[
        <DemoSliderControl
          key="thickness"
          id="thickness"
          label="Glass thickness"
          min={0}
          max={10}
          previewRenderer={(val) => `${val}px`}
        />,
        <DemoSliderControl
          key="radius"
          id="radius"
          label="Edge blur radius"
          min={0}
          max={16}
          previewRenderer={(val) => `${val}px`}
        />,
      ]}
    >
      {({ thickness, radius }) => {
        return (
          <>
            <Wrapper>
              <Header>
                <Backdrop />
                <Edge
                  style={{
                    '--thickness': thickness + 'px',
                    '--radius': radius + 'px',
                  }}
                />
              </Header>
              <BouncingBall
                style={{ animationPlayState: playState }}
              />
            </Wrapper>
            <Actions>
              <Button
                Icon={playState === 'running' ? Pause : Play}
                backgroundColor="hsl(175deg 60% 25%)"
                highlightColor="hsl(168deg 70% 35%)"
                onClick={() =>
                  setPlayState(
                    playState === 'running' ? 'paused' : 'running'
                  )
                }
              >
                Play/Pause Animation
              </Button>
            </Actions>
          </>
        );
      }}
    </Demo>
  );
}

const Wrapper = styled(Space)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  isolation: isolate;
  border: 1px solid var(--color-content-outline);
`;

const Header = styled.div`
  position: relative;
  height: 100px;
  width: 100%;
  z-index: 2;
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  height: 200%;
  background: hsl(0deg 0% 100% / 0.1);
  pointer-events: none;
  backdrop-filter: blur(16px);
  mask-image: linear-gradient(
    to bottom,
    black 0,
    black 50%,
    transparent 50%
  );
`;

interface EdgeProps {
  style: {
    '--thickness': string;
    '--radius': string;
  };
}
const Edge = styled.div<EdgeProps>`
  position: absolute;
  inset: 0;
  height: 100%;
  background: hsl(0deg 0% 100% / 0.2);
  backdrop-filter: blur(var(--radius)) brightness(1.2);
  transform: translateY(100%);
  pointer-events: none;
  mask-image: linear-gradient(
    to bottom,
    black 0,
    black var(--thickness),
    transparent var(--thickness)
  );
`;

const BouncingBall = styled.div`
  --size: 128px;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  background: hsl(45deg 100% 50%);
  animation: bounce 1000ms alternate infinite ease-in-out;

  @keyframes bounce {
    0% {
      transform: translateY(-20px);
    }

    100% {
      transform: translateY(20px);
    }
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
`;

const Button = styled(ButtonPlayful)`
  outline: 1px solid var(--color-gray-200);
`;

export default GlassyEdge;
